import React from "react";
import Image from "next/image";

import { isVideoFile, videoPosterUrl } from "@/lib/media";

interface MediaPreviewProps {
  src?: string | string[] | { url?: string } | null;
  alt?: string;
  className?: string;
  poster?: string;
  autoPlay?: boolean;
}

const DEFAULT_MEDIA = "/assets/images/property-default.svg";

function normalizeSrc(src: MediaPreviewProps["src"]): string {
  if (!src) return "";
  if (typeof src === "string") return src;
  if (Array.isArray(src) && src.length > 0 && typeof src[0] === "string")
    return src[0];
  if (
    typeof src === "object" &&
    src !== null &&
    "url" in src &&
    typeof src.url === "string"
  )
    return src.url;
  return "";
}

function isSvg(url: string): boolean {
  return /\.svg(?:$|[?#])/i.test(url);
}

/**
 * One still per card, always the same shape.
 *
 * - A video stays a `<video>`: the `poster` attribute paints the still while it
 *   buffers, and the first frame covers the case where no poster exists. It
 *   used to be swapped for a `<Image>` of the derived `-poster.jpg`, which made
 *   every video without a generated poster render as a broken image.
 * - Photos go through `next/image`: on-demand AVIF/WebP with a responsive
 *   `srcset`. Nothing is pre-generated in S3 (see the derivative policy).
 * - Video only autoplays when the caller explicitly asks for it.
 */
const MediaPreview: React.FC<MediaPreviewProps> = ({
  src,
  alt,
  className = "",
  poster,
  autoPlay = false,
}) => {
  const url = normalizeSrc(src);

  if (!url) {
    return (
      <div className="relative w-full h-[200px]">
        <Image
          src={DEFAULT_MEDIA}
          alt={alt || "default media"}
          fill
          sizes="(max-width: 640px) 100vw, 350px"
          unoptimized
          className={`object-cover ${className}`}
        />
      </div>
    );
  }

  const isVideo = isVideoFile(url);
  const posterUrl = poster || (isVideo ? videoPosterUrl(url) : undefined);

  return (
    <div className="relative w-full h-[200px]">
      {isVideo ? (
        <video
          src={url}
          poster={posterUrl || undefined}
          className={`w-full h-full object-cover ${className}`}
          muted
          loop
          playsInline
          preload="metadata"
          autoPlay={autoPlay}
        />
      ) : isSvg(url) ? (
        // next/image cannot optimise SVGs; serve them as-is.
        <Image
          src={url}
          alt={alt || "media preview"}
          fill
          sizes="(max-width: 640px) 100vw, 350px"
          unoptimized
          className={`object-cover ${className}`}
        />
      ) : (
        <Image
          src={url}
          alt={alt || "media preview"}
          fill
          sizes="(max-width: 640px) 100vw, 350px"
          className={`object-cover ${className}`}
        />
      )}
    </div>
  );
};

export default MediaPreview;
